import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {CommentAnnotationModel} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {CommentModel} from '../../../../../models/api/commentModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {getMentionableAliases} from '../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserAlias} from '../../../../../store/data/teammates/teammatesSelectors';
import {CommentBodyWithUnfurls} from '../../activity/comment/commentBodyWithUnfurls';
import {CommentHeaderIcon} from '../../activity/comment/commentHeaderIcon';
import {maybeRenderAnnotationsHighlightViewerWrapper} from '../../activity/comment/commentViewer';
import {SynopsisCellHeaderContainer} from './synopsisCellHeaderContainer';
import {SynopsisPinnedActivityItem} from './synopsisHelpers';
import {SynopsisQuickAction, SynopsisQuickActions, SynopsisQuickActionsEnum} from './synopsisQuickAction';
import {useSynopsisViewerContext} from './synopsisViewerContext';

/*
 * Props.
 */

interface SynopsisPinnedCommentProps {
  item: SynopsisPinnedActivityItem;
  comment: CommentModel;
  author: TeammateModel;
  referencedComment: CommentModel | undefined;
  referencedAuthor: TeammateModel | undefined;
  referencedRecipient: VisualRecipientModel | undefined;
  attachments: ReadonlyArray<AttachmentModel>;
  annotations: ReadonlyArray<CommentAnnotationModel>;
  panelId: ViewerPanelId;
}

/*
 * Styles.
 */

const StyledCellDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  border-radius: 12px;
  background-color: ${(p) => p.theme.message.readCommentBackground};
`;

const StyledCellDetailButton = styled(ChromelessButton)`
  position: relative;
  padding: 4px 8px;
  border-radius: 8px;
  margin: 0 4px;

  background-color: ${(p) => p.theme.message.readCommentBackground};

  &:hover {
    background-color: ${(p) => p.theme.message.readCommentBackground};
  }
`;

const StyledInnerDiv = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  grid-template-areas: 'status content';
  grid-gap: 8px;
`;

const StyledStatusDiv = styled.div`
  grid-area: status;
  margin-top: 2px;
`;

const StyledBodyWrapper = styled.div`
  background-color: ${(p) => p.theme.alphas.gray10};
  border-radius: 8px;
  padding: 4px 8px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.black30};
  }
`;

/*
 * Component.
 */

export const SynopsisPinnedComment: FC<SynopsisPinnedCommentProps> = ({
  item,
  comment,
  author,
  referencedAuthor,
  referencedComment,
  referencedRecipient,
  attachments,
  annotations,
  panelId,
}) => {
  const theme = useUserTheme();
  const {onJumpToItem, onUnpinActivity, namespace, setCopyText} = useSynopsisViewerContext();
  const userAlias = useAppSelector(getUserAlias);
  const mentionableAliases = useAppSelector(getMentionableAliases);
  const {activityId} = item;

  const textWrapper = (child: ReactNode) =>
    maybeRenderAnnotationsHighlightViewerWrapper(child, panelId, comment, annotations);

  // Use PermissionCheck to align with commentOperations.tsx, but this will always return allowed since we're not passing a permission.
  const actions = (
    <PermissionCheck>
      {(permissionResult) => (
        <SynopsisQuickAction
          actionId={SynopsisQuickActionsEnum.UNPIN}
          permissionResult={permissionResult}
          onClick={() => onUnpinActivity(activityId)}
        />
      )}
    </PermissionCheck>
  );

  return (
    <StyledCellDiv onMouseEnter={() => setCopyText(comment.text)} onMouseLeave={() => setCopyText('')}>
      <SynopsisCellHeaderContainer
        teammateIds={[author.id]}
        timestamp={comment.sentAt}
        namespace={namespace}
        panelId={panelId}
      />
      <StyledCellDetailButton onClick={() => onJumpToItem({activityId, itemType: item.type})}>
        <SynopsisQuickActions actions={actions} right={1.5} bottom={2}>
          <StyledInnerDiv>
            <StyledStatusDiv>
              <CommentHeaderIcon iconName="pinFilledTiny" backgroundColor={theme.palette.blue.shade40} />
            </StyledStatusDiv>
            <StyledBodyWrapper>
              <CommentBodyWithUnfurls
                comment={comment}
                referencedAuthor={referencedAuthor}
                referencedComment={referencedComment}
                referencedRecipient={referencedRecipient}
                mentionableAliases={mentionableAliases}
                userAlias={userAlias}
                attachments={attachments}
                annotations={annotations}
                namespace={namespace}
                panelId={panelId}
                shouldAlwaysUseMultiAttachmentStyle
                shouldTruncateAnnotationText
                isJumpPointerDisabled
                noPadding
                textWrapper={textWrapper}
              />
            </StyledBodyWrapper>
          </StyledInnerDiv>
        </SynopsisQuickActions>
      </StyledCellDetailButton>
    </StyledCellDiv>
  );
};
